import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button } from '@resy/ui-kitchen';
import Minus from '../../../../../../ui-kitchen/src/svg/icons/ic_remove_20x20.svg';
import Plus from '../../../../../../ui-kitchen/src/svg/icons/ic_add_20x20.svg';
import { fetchS3JsonFile, handleMktAnalytics } from '../AmexUtils';
import { CTA_EVENT_NAME, TERMS_FOLDER_NAME, TERMS_FILE_NAME } from '../constants';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexTerms.scss';
import Chevron from '../../../images/amexrga/chevron-left.svg';

const AmexTerms = (
  {
    baseRequestUrl,
    hash,
    termsConfig: {
      expanded_initially: termsActive,
      hide_section: hideSection,
      has_imported_terms: hasImportedTerms,
      subsections = [],
      title,
      event_tag: eventTag,
      icon_plus_minus: iconPlusMinus,
      icon_position: iconPosition,
      bg_color: bgColor,
    },
    aboveTermsConfig = {},
    userLanguage,
    deepLink,
    Analytics,
    handleTagging,
    taggingConfig,
    eventAnalytics,
  }
) => {
  const hideTerms = hideSection || !title || (subsections.length === 0);
  if (hideTerms) return false;

  const {
    hide_section: hideAboveTermsSection,
    copy: aboveTermsCopy = '',
  } = aboveTermsConfig;
  const hideAboveTerms = Object.keys(aboveTermsConfig).length === 0 || hideAboveTermsSection || !aboveTermsCopy;
  const extraParams = taggingConfig ? ({ action_name: taggingConfig?.terms?.action_name ? taggingConfig?.terms?.action_name : title }) : null;

  const [termsContent, setTermsContent] = useState(subsections);
  const [isTermsActive, setIsTermsActive] = useState(termsActive);
  const sectionId = deepLink;
  const renderTermsContent = () => termsContent
    .sort((a, b) => {
      if (a?.order_id && b?.order_id) return a.order_id > b.order_id ? 1 : -1;
      return a?.order_id ? -1 : 1;
    })
    .map((terms) => ((terms?.copyArray) && (terms?.copyArray
      .map((termsCopy) => (termsCopy?.copy) && (
        <li key={`${termsCopy.copy}`}>
          {termsCopy.title && (
          <h3
            className="AmexTerms__section-title"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizeString(termsCopy.title),
            }}
          />
          )}
          <div
            className="AmexTerms__text"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizeString(termsCopy.copy),
            }}
          />
        </li>
      ))))
    || ((terms?.copy) && (
    <li key={`${terms.copy}_${terms.order_id}`}>
      {terms.title && (
      <h3
        className="AmexTerms__section-title"
        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
          __html: sanitizeString(terms.title),
        }}
      />
      )}
      <div
        className="AmexTerms__text"
        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
          __html: sanitizeString(terms.copy),
        }}
      />
    </li>
    )));

  const updateTermsDataWithRefData = (arrayData, referenceValues) => {
    let arrayToObjects = [];
    arrayData?.forEach((dataItem) => {
      arrayToObjects = [...arrayToObjects, ...dataItem.dataArray];
    });
    const updatedDisplayData = arrayToObjects?.map((dataItem) => {
      referenceValues?.forEach((refItem) => {
        if (dataItem?.has_dynamic_copy) {
          dataItem = {
            ...dataItem,
            copy: dataItem?.copy?.replace(`{${refItem.code}}`, refItem.value),
          };
        }
      });
      return dataItem;
    });
    return updatedDisplayData;
  };

  const importTermsWithDataOrder = async () => {
    const importedTerms = await fetchS3JsonFile(baseRequestUrl, TERMS_FOLDER_NAME, TERMS_FILE_NAME, true);
    const termsCopy = termsContent.slice();
    const updatedTerms = termsCopy
      .map((item) => {
        const importedTermsLangData = importedTerms && (importedTerms[item.terms_type]?.data
          ? importedTerms[item.terms_type]?.data[userLanguage]
          : importedTerms[item.terms_type]?.[userLanguage]);

        if (!importedTermsLangData) return item;

        const importedTermsConfig = importedTerms?.[item.terms_type]?.[item.config];

        // example 'gda'
        if (!importedTermsConfig) {
          return (importedTermsLangData
            ? { ...item, copy: importedTermsLangData } : item);
        }

        const termsDataOrder = importedTermsConfig.data_order?.[item.sub_type];

        const termsDynamicMappings = importedTermsConfig.dynamic_copy_mappings?.[userLanguage];

        if (item.sub_type === 'ALL') {
          if (termsDynamicMappings) {
            let updatedDisplayData = [];
            const referenceValues = termsDynamicMappings.ALL;

            const termsDataUnordered = [];

            termsDataOrder?.forEach((orderItem, index) => {
              termsDataUnordered.push({ id: index, dataArray: importedTermsLangData[orderItem] });
            });

            termsDataUnordered?.sort((a, b) => a.id - b.id);
            updatedDisplayData = updateTermsDataWithRefData(termsDataUnordered, referenceValues);
            return (updatedDisplayData
              ? { ...item, copyArray: updatedDisplayData } : {});
          }

          const termsDataUnordered = [];

          termsDataOrder?.forEach((orderItem, index) => {
            termsDataUnordered?.push({ id: index, dataArray: importedTermsLangData[orderItem] });
          });

          termsDataUnordered?.sort((a, b) => a.id - b.id);
          let arrayToObjects = [];
          termsDataUnordered?.forEach((dataItem) => {
            arrayToObjects = [...arrayToObjects, ...dataItem.dataArray];
          });

          return (arrayToObjects
            ? { ...item, copyArray: arrayToObjects } : {});
        }

        if (item.sub_type !== 'ALL') {
          if (termsDynamicMappings) {
            let updatedDisplayData = [];

            // find the sub_types from dynamicCopyMappings
            const subTypeArr = Object.keys(termsDynamicMappings);

            let referenceValues = [];
            const termsDataUnordered = [];

            subTypeArr?.forEach((subTypeItem) => {
              referenceValues = [...referenceValues, ...termsDynamicMappings[subTypeItem]];
            });

            termsDataOrder?.forEach((orderItem, orderIndex) => {
              termsDataUnordered.push({
                id: orderIndex,
                dataArray: importedTermsLangData[orderItem],
              });
            });

            termsDataUnordered?.sort((a, b) => a.id - b.id);

            updatedDisplayData = updateTermsDataWithRefData(termsDataUnordered, referenceValues);

            return (updatedDisplayData
              ? { ...item, copyArray: updatedDisplayData } : {});
          }

          const termsDataUnordered = [];

          termsDataOrder?.forEach((orderItem, index) => {
            termsDataUnordered?.push({ id: index, dataArray: importedTermsLangData[orderItem] });
          });

          termsDataUnordered?.sort((a, b) => a.id - b.id);
          let arrayToObjects = [];
          termsDataUnordered?.forEach((dataItem) => {
            arrayToObjects = [...arrayToObjects, ...dataItem.dataArray];
          });

          return (arrayToObjects
            ? { ...item, copyArray: arrayToObjects } : {});
        }

        return null;
      });
    setTermsContent(updatedTerms);
  };

  useEffect(() => {
    let isSubscribed = true;

    if (hasImportedTerms && isSubscribed) {
      importTermsWithDataOrder();
    }

    return () => {
      isSubscribed = false;
    };
  }, []);

  useEffect(() => {
    if (hash && hash === sectionId) setIsTermsActive(true);
  }, [hash]);

  const sanitizedAboveTermsCopy = sanitizeString(aboveTermsCopy);

  const Icon = iconPlusMinus ? (isTermsActive ? Minus : Plus) : Chevron; // eslint-disable-line no-nested-ternary

  const renderIcon = (
    <span className="AmexTerms__iconwrapper">
      <Icon className={cx('AmexTerms__icon', [iconPlusMinus ? 'icon-plus-minus' : 'icon-chevron'])} aria-hidden="true" />
    </span>
  );

  return (
    <section
      className={cx('AmexTerms', { theme: !!bgColor })}
      style={{ backgroundColor: bgColor }}
    >
      <div className="container">
        <div className="AmexTerms__body">
          {!hideAboveTerms && (
            <div className="AmexTerms__above-terms">
              <div
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizedAboveTermsCopy,
                }}
              />
            </div>
          )}

          <h2
            id={sectionId}
            className={`AmexTerms__title ${isTermsActive ? 'active' : ''}`}
          >
            <Button
              variant="no-style"
              type="button"
              id="AmexTermsTitle"
              aria-controls="AmexTermsContent"
              className={cx('AmexTerms__button', {
                active: isTermsActive,
                'icon-right': iconPosition === 'right',
                'icon-plus-minus': iconPlusMinus,
              })}
              aria-expanded={isTermsActive}
              onClick={() => {
                setIsTermsActive(!isTermsActive);
                if (!eventTag) {
                  handleMktAnalytics(Analytics?.event || eventAnalytics, CTA_EVENT_NAME, {
                    action_name: 'Terms and Conditions - Click',
                  });
                } else if (eventTag) {
                  handleTagging(eventTag, extraParams);
                } else {
                  handleTagging(extraParams);
                }
              }}
            >
              {renderIcon}
              {title}
            </Button>
          </h2>
          <div
            aria-labelledby="AmexTermsTitle"
            id="AmexTermsContent"
            className={`AmexTerms__content ${isTermsActive ? 'is-open' : ''}`}
            aria-hidden={!isTermsActive}
            tabIndex="-1"
          >
            <div className="AmexTerms__inner">
              <ol
                className="AmexTerms__items"
              >
                {termsContent && renderTermsContent()}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

AmexTerms.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  hash: PropTypes.string,
  termsConfig: PropTypes.shape({
    expanded_initially: PropTypes.bool,
    hide_section: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    event_tag: PropTypes.string.isRequired,
    has_imported_terms: PropTypes.bool,
    has_dynamic_copy: PropTypes.bool.isRequired,
    icon_plus_minus: PropTypes.bool,
    icon_position: PropTypes.string,
    bg_color: PropTypes.string,
    subsections: PropTypes.arrayOf(PropTypes.shape(
      {
        order_id: PropTypes.number,
        title: PropTypes.string,
        copy: PropTypes.string,
      }
    )).isRequired,
  }),
  aboveTermsConfig: PropTypes.shape({
    hide_section: PropTypes.bool,
    copy: PropTypes.string,
  }),
  userLanguage: PropTypes.string,
  deepLink: PropTypes.string,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  handleTagging: PropTypes.func,
  taggingConfig: PropTypes.shape({
    event_name: PropTypes.string.isRequired,
    terms: PropTypes.shape({
      action_name: PropTypes.string.isRequired,
    }),
  }),
  eventAnalytics: PropTypes.func,
};

AmexTerms.defaultProps = {
  hash: null,
  termsConfig: {
    expanded_initially: false,
  },
  aboveTermsConfig: {},
  userLanguage: 'en-US',
  deepLink: 'terms',
  taggingConfig: null,
  handleTagging: () => {},
  eventAnalytics: null,
};

export default AmexTerms;
